// pages/FinancePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    DollarSign, Clock, AlertTriangle, Receipt, FileText, Download,
    TrendingUp, TrendingDown, Calendar, Filter, Search, Plus,
    CreditCard, Building2, User, Package, Truck, Eye, Edit,
    CheckCircle, XCircle, Timer, Zap, BarChart3, PieChart
} from 'lucide-react';
import { Card, Button, Badge, Input, Select, Table, Pagination } from '../components/ui';

const FinancePage = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('overview');
    const [loading, setLoading] = useState(false);
    const [dateRange, setDateRange] = useState('month');
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        customer: '',
        dateFrom: '',
        dateTo: ''
    });

    // Mock financial data
    const financialOverview = {
        totalRevenue: { value: 2450000, change: 15.2, trend: 'up' },
        pendingPayments: { value: 185000, change: -8.5, trend: 'down' },
        detentionCharges: { value: 75000, change: 25.3, trend: 'up' },
        profitMargin: { value: 18.5, change: 2.1, trend: 'up' }
    };

    const detentionData = [
        {
            id: 'DET001',
            jobId: 'JOB003',
            container: 'CONT345678',
            customer: 'Infosys Limited',
            freeTime: 72,
            usedTime: 95,
            excessTime: 23,
            ratePerHour: 500,
            totalCharge: 11500,
            status: 'pending',
            createdAt: '2024-03-15T08:00:00',
            dueDate: '2024-03-25T23:59:59'
        },
        {
            id: 'DET002',
            jobId: 'JOB007',
            container: 'CONT456789',
            customer: 'Tata Motors Ltd',
            freeTime: 48,
            usedTime: 72,
            excessTime: 24,
            ratePerHour: 450,
            totalCharge: 10800,
            status: 'paid',
            createdAt: '2024-03-14T10:30:00',
            dueDate: '2024-03-24T23:59:59'
        },
        {
            id: 'DET003',
            jobId: 'JOB012',
            container: 'CONT789123',
            customer: 'Reliance Industries',
            freeTime: 96,
            usedTime: 124,
            excessTime: 28,
            ratePerHour: 600,
            totalCharge: 16800,
            status: 'overdue',
            createdAt: '2024-03-12T14:15:00',
            dueDate: '2024-03-22T23:59:59'
        }
    ];

    const invoiceData = [
        {
            id: 'INV-2024-001',
            jobId: 'JOB001',
            customer: 'Tata Motors Ltd',
            amount: 15000,
            status: 'paid',
            issueDate: '2024-03-10T00:00:00',
            dueDate: '2024-03-25T00:00:00',
            paidDate: '2024-03-18T00:00:00',
            services: ['Transportation', 'Loading/Unloading'],
            paymentMethod: 'Bank Transfer'
        },
        {
            id: 'INV-2024-002',
            jobId: 'JOB002',
            customer: 'Maruti Suzuki',
            amount: 8500,
            status: 'pending',
            issueDate: '2024-03-12T00:00:00',
            dueDate: '2024-03-27T00:00:00',
            paidDate: null,
            services: ['Transportation'],
            paymentMethod: null
        },
        {
            id: 'INV-2024-003',
            jobId: 'JOB004',
            customer: 'Coal India Ltd',
            amount: 17500,
            status: 'overdue',
            issueDate: '2024-03-08T00:00:00',
            dueDate: '2024-03-23T00:00:00',
            paidDate: null,
            services: ['Transportation', 'Documentation'],
            paymentMethod: null
        }
    ];

    const StatCard = ({ icon: Icon, title, value, change, trend, prefix = '', suffix = '' }) => (
        <Card className="p-6 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-r ${title.includes('Revenue') || title.includes('Profit') ? 'from-green-500 to-green-600' :
                        title.includes('Pending') ? 'from-yellow-500 to-yellow-600' :
                            title.includes('Detention') ? 'from-red-500 to-red-600' :
                                'from-blue-500 to-blue-600'
                    } shadow-lg`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
                <div className={`flex items-center text-sm font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }`}>
                    {trend === 'up' ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                    {change > 0 ? '+' : ''}{change}%
                </div>
            </div>

            <div>
                <h3 className="text-sm font-medium text-gray-600">{title}</h3>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                    {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
                </p>
            </div>
        </Card>
    );

    const DetentionCalculator = () => {
        const [calculatorData, setCalculatorData] = useState({
            freeTime: 72,
            usedTime: 0,
            ratePerHour: 500
        });

        const excessTime = Math.max(0, calculatorData.usedTime - calculatorData.freeTime);
        const totalCharge = excessTime * calculatorData.ratePerHour;

        return (
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Timer className="w-5 h-5 mr-2" />
                    Detention Calculator
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <Input
                        label="Free Time (hours)"
                        type="number"
                        value={calculatorData.freeTime}
                        onChange={(e) => setCalculatorData(prev => ({ ...prev, freeTime: Number(e.target.value) }))}
                    />
                    <Input
                        label="Used Time (hours)"
                        type="number"
                        value={calculatorData.usedTime}
                        onChange={(e) => setCalculatorData(prev => ({ ...prev, usedTime: Number(e.target.value) }))}
                    />
                    <Input
                        label="Rate per Hour (₹)"
                        type="number"
                        value={calculatorData.ratePerHour}
                        onChange={(e) => setCalculatorData(prev => ({ ...prev, ratePerHour: Number(e.target.value) }))}
                    />
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-sm text-gray-600">Excess Time</p>
                            <p className="text-xl font-bold text-gray-900">{excessTime} hours</p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-600">Total Detention Charge</p>
                            <p className="text-xl font-bold text-green-600">₹{totalCharge.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            </Card>
        );
    };

    const DetentionTable = () => {
        const columns = [
            {
                header: 'Detention Details',
                accessor: 'id',
                render: (_, detention) => (
                    <div>
                        <p className="font-medium text-blue-600">{detention.id}</p>
                        <p className="text-sm text-gray-600">Job: {detention.jobId}</p>
                        <p className="text-sm text-gray-500">{detention.container}</p>
                    </div>
                )
            },
            {
                header: 'Customer',
                accessor: 'customer',
                render: (_, detention) => (
                    <div>
                        <p className="font-medium text-gray-900">{detention.customer}</p>
                        <p className="text-sm text-gray-500">
                            Created: {new Date(detention.createdAt).toLocaleDateString()}
                        </p>
                    </div>
                )
            },
            {
                header: 'Time Details',
                accessor: 'time',
                render: (_, detention) => (
                    <div className="text-sm">
                        <div className="flex justify-between mb-1">
                            <span className="text-gray-600">Free:</span>
                            <span className="font-medium">{detention.freeTime}h</span>
                        </div>
                        <div className="flex justify-between mb-1">
                            <span className="text-gray-600">Used:</span>
                            <span className="font-medium">{detention.usedTime}h</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Excess:</span>
                            <span className="font-medium text-red-600">{detention.excessTime}h</span>
                        </div>
                    </div>
                )
            },
            {
                header: 'Charges',
                accessor: 'charges',
                render: (_, detention) => (
                    <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">₹{detention.totalCharge.toLocaleString()}</p>
                        <p className="text-sm text-gray-500">@ ₹{detention.ratePerHour}/hr</p>
                    </div>
                )
            },
            {
                header: 'Status',
                accessor: 'status',
                render: (_, detention) => {
                    const statusConfig = {
                        pending: { color: 'warning', label: 'Pending', icon: Clock },
                        paid: { color: 'success', label: 'Paid', icon: CheckCircle },
                        overdue: { color: 'danger', label: 'Overdue', icon: AlertTriangle }
                    };
                    const config = statusConfig[detention.status];
                    const StatusIcon = config.icon;

                    return (
                        <div>
                            <Badge variant={config.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {config.label}
                            </Badge>
                            <p className="text-xs text-gray-500 mt-1">
                                Due: {new Date(detention.dueDate).toLocaleDateString()}
                            </p>
                        </div>
                    );
                }
            },
            {
                header: 'Actions',
                accessor: 'actions',
                render: (_, detention) => (
                    <div className="flex space-x-2">
                        <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <Receipt className="w-4 h-4" />
                        </Button>
                    </div>
                )
            }
        ];

        return (
            <Card>
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Detention Charges</h3>
                        <div className="flex space-x-2">
                            <Button variant="outline" size="sm">
                                <Filter className="w-4 h-4 mr-2" />
                                Filter
                            </Button>
                            <Button variant="outline" size="sm">
                                <Download className="w-4 h-4 mr-2" />
                                Export
                            </Button>
                        </div>
                    </div>
                </div>

                <Table
                    columns={columns}
                    data={detentionData}
                    loading={loading}
                />
            </Card>
        );
    };

    const InvoiceTable = () => {
        const columns = [
            {
                header: 'Invoice Details',
                accessor: 'id',
                render: (_, invoice) => (
                    <div>
                        <p className="font-medium text-blue-600">{invoice.id}</p>
                        <p className="text-sm text-gray-600">Job: {invoice.jobId}</p>
                        <p className="text-xs text-gray-500">
                            Issued: {new Date(invoice.issueDate).toLocaleDateString()}
                        </p>
                    </div>
                )
            },
            {
                header: 'Customer',
                accessor: 'customer',
                render: (_, invoice) => (
                    <div>
                        <p className="font-medium text-gray-900">{invoice.customer}</p>
                        <div className="text-xs text-gray-500 mt-1">
                            {invoice.services.map((service, index) => (
                                <span key={index} className="inline-block bg-gray-100 px-2 py-1 rounded mr-1 mb-1">
                                    {service}
                                </span>
                            ))}
                        </div>
                    </div>
                )
            },
            {
                header: 'Amount',
                accessor: 'amount',
                render: (_, invoice) => (
                    <div className="text-right">
                        <p className="text-lg font-bold text-gray-900">₹{invoice.amount.toLocaleString()}</p>
                        {invoice.paymentMethod && (
                            <p className="text-sm text-gray-500">{invoice.paymentMethod}</p>
                        )}
                    </div>
                )
            },
            {
                header: 'Due Date',
                accessor: 'dueDate',
                render: (_, invoice) => (
                    <div>
                        <p className="text-sm font-medium text-gray-900">
                            {new Date(invoice.dueDate).toLocaleDateString()}
                        </p>
                        {invoice.paidDate && (
                            <p className="text-sm text-green-600">
                                Paid: {new Date(invoice.paidDate).toLocaleDateString()}
                            </p>
                        )}
                    </div>
                )
            },
            {
                header: 'Status',
                accessor: 'status',
                render: (_, invoice) => {
                    const statusConfig = {
                        pending: { color: 'warning', label: 'Pending', icon: Clock },
                        paid: { color: 'success', label: 'Paid', icon: CheckCircle },
                        overdue: { color: 'danger', label: 'Overdue', icon: XCircle }
                    };
                    const config = statusConfig[invoice.status];
                    const StatusIcon = config.icon;

                    return (
                        <Badge variant={config.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {config.label}
                        </Badge>
                    );
                }
            },
            {
                header: 'Actions',
                accessor: 'actions',
                render: (_, invoice) => (
                    <div className="flex space-x-2">
                        <Button variant="outline" size="sm">
                            <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <Download className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                            <Edit className="w-4 h-4" />
                        </Button>
                    </div>
                )
            }
        ];

        return (
            <Card>
                <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900">Invoices & Billing</h3>
                        <div className="flex space-x-2">
                            <Button variant="outline" size="sm">
                                <Filter className="w-4 h-4 mr-2" />
                                Filter
                            </Button>
                            <Button variant="primary" size="sm">
                                <Plus className="w-4 h-4 mr-2" />
                                New Invoice
                            </Button>
                        </div>
                    </div>
                </div>

                <Table
                    columns={columns}
                    data={invoiceData}
                    loading={loading}
                />
            </Card>
        );
    };

    const FinancialCharts = () => (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2" />
                    Revenue Trend
                </h3>
                <div className="h-64 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                        <BarChart3 className="w-12 h-12 text-blue-400 mx-auto mb-2" />
                        <p className="text-gray-600">Revenue trend chart will be displayed here</p>
                    </div>
                </div>
            </Card>

            <Card className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <PieChart className="w-5 h-5 mr-2" />
                    Payment Status Distribution
                </h3>
                <div className="h-64 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                        <PieChart className="w-12 h-12 text-purple-400 mx-auto mb-2" />
                        <p className="text-gray-600">Payment distribution chart will be displayed here</p>
                    </div>
                </div>
            </Card>
        </div>
    );

    const tabs = [
        { id: 'overview', label: 'Overview', icon: BarChart3 },
        { id: 'invoices', label: 'Invoices', icon: Receipt },
        { id: 'detention', label: 'Detention', icon: Timer },
        { id: 'payments', label: 'Payments', icon: CreditCard }
    ];

    return (
        <div className="p-6 space-y-6">
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                        <DollarSign className="w-6 h-6 mr-2 text-green-600" />
                        Finance & Billing
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Manage invoices, track payments, and monitor detention charges
                    </p>
                </div>

                <div className="flex items-center space-x-3 mt-4 lg:mt-0">
                    <Select
                        value={dateRange}
                        onChange={(e) => setDateRange(e.target.value)}
                        options={[
                            { value: 'week', label: 'This Week' },
                            { value: 'month', label: 'This Month' },
                            { value: 'quarter', label: 'This Quarter' },
                            { value: 'year', label: 'This Year' }
                        ]}
                    />
                    <Button variant="outline" size="sm">
                        <Download className="w-4 h-4 mr-2" />
                        Export Report
                    </Button>
                    <Button variant="primary">
                        <Plus className="w-4 h-4 mr-2" />
                        New Invoice
                    </Button>
                </div>
            </div>

            {/* Financial Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <StatCard
                    icon={DollarSign}
                    title="Total Revenue"
                    value={financialOverview.totalRevenue.value}
                    change={financialOverview.totalRevenue.change}
                    trend={financialOverview.totalRevenue.trend}
                    prefix="₹"
                />
                <StatCard
                    icon={Clock}
                    title="Pending Payments"
                    value={financialOverview.pendingPayments.value}
                    change={financialOverview.pendingPayments.change}
                    trend={financialOverview.pendingPayments.trend}
                    prefix="₹"
                />
                <StatCard
                    icon={AlertTriangle}
                    title="Detention Charges"
                    value={financialOverview.detentionCharges.value}
                    change={financialOverview.detentionCharges.change}
                    trend={financialOverview.detentionCharges.trend}
                    prefix="₹"
                />
                <StatCard
                    icon={TrendingUp}
                    title="Profit Margin"
                    value={financialOverview.profitMargin.value}
                    change={financialOverview.profitMargin.change}
                    trend={financialOverview.profitMargin.trend}
                    suffix="%"
                />
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
                <nav className="flex space-x-8">
                    {tabs.map((tab) => {
                        const TabIcon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                <div className="flex items-center">
                                    <TabIcon className="w-4 h-4 mr-2" />
                                    {tab.label}
                                </div>
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Tab Content */}
            <div className="space-y-6">
                {activeTab === 'overview' && (
                    <>
                        <FinancialCharts />
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            <DetentionCalculator />
                            <Card className="p-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h3>
                                <div className="space-y-3">
                                    {invoiceData.slice(0, 5).map((invoice) => (
                                        <div key={invoice.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                            <div className="flex items-center">
                                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                                                    <Receipt className="w-5 h-5 text-blue-600" />
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900">{invoice.id}</p>
                                                    <p className="text-sm text-gray-600">{invoice.customer}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-medium text-gray-900">₹{invoice.amount.toLocaleString()}</p>
                                                <Badge variant={invoice.status === 'paid' ? 'success' : invoice.status === 'overdue' ? 'danger' : 'warning'} size="sm">
                                                    {invoice.status}
                                                </Badge>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </div>
                    </>
                )}

                {activeTab === 'invoices' && <InvoiceTable />}

                {activeTab === 'detention' && (
                    <>
                        <DetentionCalculator />
                        <DetentionTable />
                    </>
                )}

                {activeTab === 'payments' && (
                    <Card className="p-8 text-center">
                        <CreditCard className="w-16 h-16 text-blue-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Payment Management</h3>
                        <p className="text-gray-600">Payment processing and tracking interface coming soon.</p>
                    </Card>
                )}
            </div>
        </div>
    );
};

export default FinancePage;